import { ref } from 'vue';
import { forgetMode, readStoredMode, storeMode, type ThemeMode } from './mode';

/*
 * Applying an appearance. `mode.ts` is the data; this is the wiring, and there is very little of
 * it — which is the point.
 *
 * The document carries two attributes, and they are two axes rather than one list of themes:
 *
 *   <html data-theme="green-gold" data-mode="dark">
 *          └ the family — which colours                └ the appearance — how light
 *
 * `data-mode` is written **only when somebody has chosen one**. Its absence is not a missing
 * value: `:root` in main.css is `color-scheme: light dark`, which is the browser painting
 * whichever half of every `light-dark()` the reader's system asks for. So following the system
 * costs no JavaScript, survives a system change with nothing listening, and — because the CSS is
 * right before this module has even loaded — never flashes the wrong page colour.
 *
 * `data-theme` is written always, though there is only one family to write. It is what a second
 * one — black & white, a team's colours — hangs its selector off, and writing it now means that
 * day is a block of CSS rather than a change in here. See docs/decisions/0012.
 *
 * **`<meta name="theme-color">` is deliberately left alone.** It is `pitch-700`, the brand green,
 * and it is neither theme's page colour — the seam between it and the page exists in the light
 * theme today and nobody has minded. Making it follow the appearance is a separate decision about
 * what the browser's own chrome should say, not part of having two themes.
 */

/**
 * The only family there is, and the value `data-theme` always carries.
 *
 * A region-derived default is the plan for this — see docs/ROADMAP.md — and green & gold is what
 * a region with no team of its own gets.
 */
export const DEFAULT_FAMILY = 'green-gold';

/**
 * What the reader chose, or null for "never chose — follow the system".
 *
 * A ref rather than a getter because the settings screen binds to it, in the same way components
 * read `i18n.global.locale`. Read it; `chooseMode` is the only thing that should write.
 */
export const chosenMode = ref<ThemeMode | null>(null);

/** Writes the two attributes. Removing `data-mode` is how the system gets the decision back. */
function paint(): void {
  const root = document.documentElement;

  root.dataset.theme = DEFAULT_FAMILY;
  if (chosenMode.value === null) delete root.dataset.mode;
  else root.dataset.mode = chosenMode.value;
}

/**
 * Applies an appearance, and remembers it. `null` gives the choice back to the system.
 *
 * `remember: false` is what booting uses: a stored choice being re-applied is not a new choice,
 * and writing it back would be a pointless touch of a store that may well throw.
 */
export function chooseMode(mode: ThemeMode | null, { remember = true } = {}): void {
  chosenMode.value = mode;
  paint();

  if (!remember) return;
  if (mode === null) forgetMode();
  else storeMode(mode);
}

/**
 * The appearance to open a visit in, applied before the app mounts.
 *
 * A remembered choice wins; otherwise nothing is written and the stylesheet's own default — the
 * reader's system setting — stands. A choice on the subject overrides this when `/me` lands a
 * moment later, which is the store's job and is what carries the choice from a phone to a laptop.
 */
export function bootTheme(): void {
  chooseMode(readStoredMode(), { remember: false });
}
